// Source of truth for the M2–M4 fixture catalog. Mirrors what
// backend/app/seed_catalog.py will write to prod at M5 cutover — same SKUs,
// same prices, same country/region/plan_type fields — so swapping to a live
// fetch against /api/plans later is a one-line change in src/api/plans.ts.

export interface Plan {
  id: string
  name: string
  country: string
  region: string
  plan_type: 'country' | 'regional'
  data_gb: number // 999 == unlimited
  validity_days: number
  price_cents: number
  currency: string
}

export interface CountryMeta {
  code: string
  name: string
  flag: string
  region: string
  networks: string
  // Carrier tech, when it differs from the site-wide default (4G/5G). JoyTel
  // lists India's pack as 4G only, and claiming 5G on a page that sells it
  // would be an unsubstantiated claim — the same discipline as `networks`.
  // Absent = the default; set it only on a documented exception.
  speed?: string
  popular?: boolean
  // Hero photo URL for the destination detail page. Optional — when absent,
  // the page renders the existing CSS-gradient art card. Today we point to
  // Unsplash CDN URLs (free, commercial-use, no attribution required under
  // the Unsplash License). Swap to your own hosted image any time.
  image?: string
  // Optional short trust-signal line shown under the lede on the destination
  // detail page — use it to justify a single-carrier setup or call out
  // coverage strengths. Keep to one sentence.
  network_note?: string
}

export interface RegionalMeta {
  code: string // 'EU' | 'AP' | 'CHM'
  name: string
  flag: string // emoji
  region: string
  networks: string
  scope: string // "36 countries", etc
  icon: string // emoji for banner card
  desc: string
  featured?: boolean
  // See CountryMeta.image. Same fallback semantics.
  // Locally-hosted art lives under /img/, never under a path that matches a
  // route. nginx serves the SPA with `try_files $uri $uri/ /index.html`, so a
  // public/ subdirectory named after a route shadows it: the directory exists,
  // has no index file, and nginx 403s instead of falling through to the SPA.
  // public/destinations/ took /destinations down exactly that way.
  image?: string
  // Wording for the coverage card + modal. Defaults to "countries"; set to
  // 'regions' for bundles whose legs aren't all countries — CHM covers two
  // SARs, and calling Hong Kong and Macau "countries" is both inaccurate and
  // needlessly provocative to the Chinese-speaking travelers who buy it.
  coverageUnit?: 'countries' | 'regions'
  // True when `image` is a finished card illustration that already carries
  // its own label and framing. The detail art card then renders it clean —
  // no darkening scrim, no overlaid NIMVOY · <code> label — instead of
  // treating it as a photo that needs both.
  artwork?: boolean
}

export interface Review {
  name: string
  from: string
  stars: number
  text: string
}

// ---- Plans (41 total) ----
// Kept in a single flat array so the fixture stays 1:1 with the eventual
// /api/plans response. Derived views (by country, cheapest, etc) are
// computed below.
export const PLANS: Plan[] = [
  // -- Country: China --
  { id: 'eSIM-CIMCN1G-01',   name: 'China 1 Day 1GB',          country: 'CN', region: 'asia',      plan_type: 'country',  data_gb: 1,   validity_days: 1,  price_cents: 100,  currency: 'usd' },
  { id: 'eSIM-CIMCNT5G-07',  name: 'China 7 Days 5GB',         country: 'CN', region: 'asia',      plan_type: 'country',  data_gb: 5,   validity_days: 7,  price_cents: 500,  currency: 'usd' },
  { id: 'eSIM-CIMCNT10G-07', name: 'China 7 Days 10GB',        country: 'CN', region: 'asia',      plan_type: 'country',  data_gb: 10,  validity_days: 7,  price_cents: 800,  currency: 'usd' },
  { id: 'eSIM-CN10M-07',     name: 'China 7 Days Unlimited',   country: 'CN', region: 'asia',      plan_type: 'country',  data_gb: 999, validity_days: 7,  price_cents: 1800, currency: 'usd' },
  { id: 'eSIM-CIMCNT20G-30', name: 'China 30 Days 20GB',       country: 'CN', region: 'asia',      plan_type: 'country',  data_gb: 20,  validity_days: 30, price_cents: 2000, currency: 'usd' },
  // -- Country: Japan --
  { id: 'eSIM-JPT5G-07',     name: 'Japan 7 Days 5GB',         country: 'JP', region: 'asia',      plan_type: 'country',  data_gb: 5,   validity_days: 7,  price_cents: 600,  currency: 'usd' },
  { id: 'eSIM-JPT10G-07',    name: 'Japan 7 Days 10GB',        country: 'JP', region: 'asia',      plan_type: 'country',  data_gb: 10,  validity_days: 7,  price_cents: 800,  currency: 'usd' },
  { id: 'eSIM-JP10M-07',     name: 'Japan 7 Days Unlimited',   country: 'JP', region: 'asia',      plan_type: 'country',  data_gb: 999, validity_days: 7,  price_cents: 1800, currency: 'usd' },
  // -- Country: Korea --
  { id: 'eSIM-KRT5G-07',     name: 'Korea 7 Days 5GB',         country: 'KR', region: 'asia',      plan_type: 'country',  data_gb: 5,   validity_days: 7,  price_cents: 400,  currency: 'usd' },
  { id: 'eSIM-KRT10G-07',    name: 'Korea 7 Days 10GB',        country: 'KR', region: 'asia',      plan_type: 'country',  data_gb: 10,  validity_days: 7,  price_cents: 700,  currency: 'usd' },
  { id: 'eSIM-KRMAX-07',     name: 'Korea 7 Days Unlimited',   country: 'KR', region: 'asia',      plan_type: 'country',  data_gb: 999, validity_days: 7,  price_cents: 1600, currency: 'usd' },
  // -- Country: USA --
  { id: 'eSIM-US1G-01',      name: 'USA 1 Day 1GB',            country: 'US', region: 'americas',  plan_type: 'country',  data_gb: 1,   validity_days: 1,  price_cents: 100,  currency: 'usd' },
  { id: 'eSIM-UST5G-07',     name: 'USA 7 Days 5GB',           country: 'US', region: 'americas',  plan_type: 'country',  data_gb: 5,   validity_days: 7,  price_cents: 600,  currency: 'usd' },
  { id: 'eSIM-UST10G-07',    name: 'USA 7 Days 10GB',          country: 'US', region: 'americas',  plan_type: 'country',  data_gb: 10,  validity_days: 7,  price_cents: 890,  currency: 'usd' },
  { id: 'eSIM-USMAX-07',     name: 'USA 7 Days Unlimited',     country: 'US', region: 'americas',  plan_type: 'country',  data_gb: 999, validity_days: 7,  price_cents: 1900, currency: 'usd' },
  { id: 'eSIM-UST20G-30',    name: 'USA 30 Days 20GB',         country: 'US', region: 'americas',  plan_type: 'country',  data_gb: 20,  validity_days: 30, price_cents: 1990, currency: 'usd' },
  // -- Regional: China + HK + Macau --
  { id: 'eSIM-CHM1G-01',     name: 'China + HK + Macau 1 Day 1GB',        country: 'CHM', region: 'china-region', plan_type: 'regional', data_gb: 1,   validity_days: 1,  price_cents: 100,  currency: 'usd' },
  { id: 'eSIM-CHMT5G-07',    name: 'China + HK + Macau 7 Days 5GB',       country: 'CHM', region: 'china-region', plan_type: 'regional', data_gb: 5,   validity_days: 7,  price_cents: 600,  currency: 'usd' },
  { id: 'eSIM-CHMT10G-07',   name: 'China + HK + Macau 7 Days 10GB',      country: 'CHM', region: 'china-region', plan_type: 'regional', data_gb: 10,  validity_days: 7,  price_cents: 900,  currency: 'usd' },
  { id: 'eSIM-CHM10M-07',    name: 'China + HK + Macau 7 Days Unlimited', country: 'CHM', region: 'china-region', plan_type: 'regional', data_gb: 999, validity_days: 7,  price_cents: 2500, currency: 'usd' },
  { id: 'eSIM-CHMT20G-30',   name: 'China + HK + Macau 30 Days 20GB',     country: 'CHM', region: 'china-region', plan_type: 'regional', data_gb: 20,  validity_days: 30, price_cents: 2000, currency: 'usd' },
  // -- Regional: Europe --
  { id: 'eSIM-EUCT5G-07',    name: 'Europe 7 Days 5GB',          country: 'EU', region: 'europe',  plan_type: 'regional', data_gb: 5,   validity_days: 7,  price_cents: 600,  currency: 'usd' },
  { id: 'eSIM-EUCT10G-07',   name: 'Europe 7 Days 10GB',         country: 'EU', region: 'europe',  plan_type: 'regional', data_gb: 10,  validity_days: 7,  price_cents: 990,  currency: 'usd' },
  { id: 'eSIM-EUC10M-07',    name: 'Europe 7 Days Unlimited',    country: 'EU', region: 'europe',  plan_type: 'regional', data_gb: 999, validity_days: 7,  price_cents: 2500, currency: 'usd' },
  { id: 'eSIM-EUCT5G-15',    name: 'Europe 15 Days 5GB',         country: 'EU', region: 'europe',  plan_type: 'regional', data_gb: 5,   validity_days: 15, price_cents: 700,  currency: 'usd' },
  { id: 'eSIM-EUCT10G-15',   name: 'Europe 15 Days 10GB',        country: 'EU', region: 'europe',  plan_type: 'regional', data_gb: 10,  validity_days: 15, price_cents: 1090, currency: 'usd' },
  // -- Regional: Asia-Pacific --
  { id: 'eSIM-APACAT3G-03',  name: 'Asia-Pacific 3 Days 3GB',    country: 'AP', region: 'asia-pacific', plan_type: 'regional', data_gb: 3,   validity_days: 3,  price_cents: 390,  currency: 'usd' },
  { id: 'eSIM-APACAM10-03',  name: 'Asia-Pacific 3 Days Unlimited', country: 'AP', region: 'asia-pacific', plan_type: 'regional', data_gb: 999, validity_days: 3,  price_cents: 1260, currency: 'usd' },
  { id: 'eSIM-APACAT3G-07',  name: 'Asia-Pacific 7 Days 3GB',    country: 'AP', region: 'asia-pacific', plan_type: 'regional', data_gb: 3,   validity_days: 7,  price_cents: 450,  currency: 'usd' },
  { id: 'eSIM-APACAT5G-07',  name: 'Asia-Pacific 7 Days 5GB',    country: 'AP', region: 'asia-pacific', plan_type: 'regional', data_gb: 5,   validity_days: 7,  price_cents: 650,  currency: 'usd' },
  { id: 'eSIM-APACAT10G-07', name: 'Asia-Pacific 7 Days 10GB',   country: 'AP', region: 'asia-pacific', plan_type: 'regional', data_gb: 10,  validity_days: 7,  price_cents: 1150, currency: 'usd' },
  { id: 'eSIM-APACAM10-07',  name: 'Asia-Pacific 7 Days Unlimited', country: 'AP', region: 'asia-pacific', plan_type: 'regional', data_gb: 999, validity_days: 7,  price_cents: 2890, currency: 'usd' },
  { id: 'eSIM-APACAT5G-15',  name: 'Asia-Pacific 15 Days 5GB',   country: 'AP', region: 'asia-pacific', plan_type: 'regional', data_gb: 5,   validity_days: 15, price_cents: 750,  currency: 'usd' },
  { id: 'eSIM-APACAT10G-15', name: 'Asia-Pacific 15 Days 10GB',  country: 'AP', region: 'asia-pacific', plan_type: 'regional', data_gb: 10,  validity_days: 15, price_cents: 1250, currency: 'usd' },
  { id: 'eSIM-APACAT20G-15', name: 'Asia-Pacific 15 Days 20GB',  country: 'AP', region: 'asia-pacific', plan_type: 'regional', data_gb: 20,  validity_days: 15, price_cents: 1990, currency: 'usd' },
  { id: 'eSIM-APACAM10-15',  name: 'Asia-Pacific 15 Days Unlimited', country: 'AP', region: 'asia-pacific', plan_type: 'regional', data_gb: 999, validity_days: 15, price_cents: 5990, currency: 'usd' },
  { id: 'eSIM-APACAT5G-30',  name: 'Asia-Pacific 30 Days 5GB',   country: 'AP', region: 'asia-pacific', plan_type: 'regional', data_gb: 5,   validity_days: 30, price_cents: 850,  currency: 'usd' },
  { id: 'eSIM-APACAT10G-30', name: 'Asia-Pacific 30 Days 10GB',  country: 'AP', region: 'asia-pacific', plan_type: 'regional', data_gb: 10,  validity_days: 30, price_cents: 1350, currency: 'usd' },
  { id: 'eSIM-APACAT20G-30', name: 'Asia-Pacific 30 Days 20GB',  country: 'AP', region: 'asia-pacific', plan_type: 'regional', data_gb: 20,  validity_days: 30, price_cents: 2290, currency: 'usd' },
  { id: 'eSIM-APACAT50G-30', name: 'Asia-Pacific 30 Days 50GB',  country: 'AP', region: 'asia-pacific', plan_type: 'regional', data_gb: 50,  validity_days: 30, price_cents: 4990, currency: 'usd' },
  { id: 'eSIM-APACAM10-30',  name: 'Asia-Pacific 30 Days Unlimited', country: 'AP', region: 'asia-pacific', plan_type: 'regional', data_gb: 999, validity_days: 30, price_cents: 9990, currency: 'usd' },
]

// ---- Country metadata (only countries with plans) ----
// Image URLs: Unsplash CDN, free under the Unsplash License. Sized w=1600
// q=80 — large enough for retina hero treatment without bloating the page.
const _UNSPLASH = (id: string) => `https://images.unsplash.com/${id}?w=1600&q=80&auto=format&fit=crop`

// Self-hosted `/img/<code>-card.jpg` cards (South America + Morocco) are CC0
// via rawpixel, sourced through the Openverse API — public domain, no
// attribution or share-alike obligation. They are self-hosted rather than
// hotlinked because CC0 guarantees the licence, not the URL's lifetime.
//
// `uy-card.jpg` and `gf-card.jpg` are founder-picked from Unsplash (free
// commercial use, no attribution) and self-hosted for the same reason.
//
// Every country now has an image, but the fallback below still matters for
// the next one added: with no `image`, the page renders a branded navy
// gradient carrying the flag and "NIMVOY · <code>" (see DestinationDetail).
// Nothing errors, so a missing photo ships unnoticed — it did for all eight
// South America pages. Two traps when sourcing a replacement: a
// `plus.unsplash.com/premium_photo-*` URL is Unsplash+, a PAID subscription
// licence, not the free Unsplash License; and stock searches for obscure
// destinations return the wrong country (French Guiana kept returning
// Hawaii). Check the photo's stated location, and don't trust Unsplash's
// auto-generated alt text — it described the Punta del Este sculpture as
// "gray stone on brown wooden table".

export const COUNTRIES: CountryMeta[] = [
  { code: 'US', name: 'United States',        flag: '🇺🇸', region: 'americas', networks: 'Verizon, T-Mobile',               popular: true, image: _UNSPLASH('photo-1699025095617-5570bc591fb4') },
  { code: 'JP', name: 'Japan',                flag: '🇯🇵', region: 'asia',     networks: 'NTT Docomo, SoftBank',           popular: true, image: _UNSPLASH('photo-1528164344705-47542687000d') },
  { code: 'KR', name: 'South Korea',          flag: '🇰🇷', region: 'asia',     networks: 'SK Telecom, KT', image: _UNSPLASH('photo-1762267616547-6d6cd4adabc3') },
  { code: 'CN', name: 'China',                flag: '🇨🇳', region: 'asia',     networks: 'China Mobile',                   popular: true, image: _UNSPLASH('photo-1508804185872-d7badad00f7d'), network_note: 'Backed by China Mobile — the most reliable network in China with the best nationwide coverage.' },
  { code: 'TW', name: 'Taiwan',               flag: '🇹🇼', region: 'asia',     networks: 'Chunghwa Telecom, Far EasTone', image: _UNSPLASH('photo-1601534621622-8587a8a0da11') },
  { code: 'TR', name: 'Turkey',               flag: '🇹🇷', region: 'europe',   networks: 'Turkcell, Vodafone',             popular: true, image: _UNSPLASH('photo-1604156789095-3348604c0f43') },
  { code: 'AE', name: 'United Arab Emirates', flag: '🇦🇪', region: 'asia',     networks: 'Etisalat, du',                   image: _UNSPLASH('photo-1512453979798-5ea266f8880c') },
  { code: 'TH', name: 'Thailand',             flag: '🇹🇭', region: 'asia',     networks: 'AIS, TrueMove H',                popular: true, image: _UNSPLASH('photo-1528181304800-259b08848526') },
  { code: 'IN', name: 'India',                 flag: '🇮🇳', region: 'asia',     networks: 'Airtel, Vodafone Idea, Jio', speed: '4G', image: '/img/in-card.jpg' },
  { code: 'HK', name: 'Hong Kong',            flag: '🇭🇰', region: 'asia',     networks: 'China Telecom HK, HKT',          image: _UNSPLASH('photo-1620015092538-e33c665fc181') },
  { code: 'AL', name: 'Albania',              flag: '🇦🇱', region: 'europe',   networks: 'One.al, Vodafone',               image: _UNSPLASH('photo-1705405999485-188af37e0462') },
  { code: 'DZ', name: 'Algeria',              flag: '🇩🇿', region: 'africa',   networks: 'Djezzy, Ooredoo',                image: _UNSPLASH('photo-1670015239006-610536cc0593') },
  { code: 'MA', name: 'Morocco',              flag: '🇲🇦', region: 'africa',   networks: 'IAM (Maroc Telecom), inwi', image: '/img/ma-card.jpg' },
  { code: 'AD', name: 'Andorra',              flag: '🇦🇩', region: 'europe',   networks: 'Andorra Telecom',                image: _UNSPLASH('photo-1614678096587-0ad50faf1603') },
  { code: 'FR', name: 'France',               flag: '🇫🇷', region: 'europe',   networks: 'Orange, Bouygues, Free',         popular: true, image: _UNSPLASH('photo-1502602898657-3e91760cbb34') },
  { code: 'GR', name: 'Greece',               flag: '🇬🇷', region: 'europe',   networks: 'Vodafone, Nova, Cosmote',        popular: true, image: _UNSPLASH('photo-1580502304784-8985b7eb7260') },
  { code: 'IT', name: 'Italy',                flag: '🇮🇹', region: 'europe',   networks: 'Vodafone, iliad',                popular: true, image: _UNSPLASH('photo-1509024644558-2f56ce76c490') },
  { code: 'MX', name: 'Mexico',               flag: '🇲🇽', region: 'americas', networks: 'Telcel, AT&T Mexico',            popular: true, image: _UNSPLASH('photo-1547686669-9a8cb1a22d91') },
  { code: 'CA', name: 'Canada',               flag: '🇨🇦', region: 'americas', networks: 'Rogers', image: _UNSPLASH('photo-1562375121-ea1bc3e8fc10') },
  { code: 'BR', name: 'Brazil',                flag: '🇧🇷', region: 'americas', networks: 'TIM, Vivo',               popular: true, image: '/img/br-card.jpg' },
  { code: 'AR', name: 'Argentina',             flag: '🇦🇷', region: 'americas', networks: 'Movistar', image: '/img/ar-card.jpg' },
  { code: 'CL', name: 'Chile',                 flag: '🇨🇱', region: 'americas', networks: 'WOM, Movistar', image: '/img/cl-card.jpg' },
  { code: 'CO', name: 'Colombia',              flag: '🇨🇴', region: 'americas', networks: 'Movistar', image: '/img/co-card.jpg' },
  { code: 'PE', name: 'Peru',                  flag: '🇵🇪', region: 'americas', networks: 'Movistar', image: '/img/pe-card.jpg' },
  { code: 'EC', name: 'Ecuador',               flag: '🇪🇨', region: 'americas', networks: 'Movistar', image: '/img/ec-card.jpg' },
  { code: 'UY', name: 'Uruguay',               flag: '🇺🇾', region: 'americas', networks: 'Movistar', image: '/img/uy-card.jpg' },
  { code: 'GF', name: 'French Guiana',         flag: '🇬🇫', region: 'americas', networks: 'Digicel, Bouygues', image: '/img/gf-card.jpg' },
  { code: 'SG', name: 'Singapore',            flag: '🇸🇬', region: 'asia',     networks: 'Simba, StarHub',                 image: _UNSPLASH('photo-1525625293386-3f8f99389edd') },
  { code: 'MY', name: 'Malaysia',             flag: '🇲🇾', region: 'asia',     networks: 'Digi, Celcom, U Mobile',         image: _UNSPLASH('photo-1596422846543-75c6fc197f07') },
  { code: 'ID', name: 'Indonesia',            flag: '🇮🇩', region: 'asia',     networks: 'Telkomsel, XL, Indosat',         image: _UNSPLASH('photo-1537996194471-e657df975ab4') },
  { code: 'PH', name: 'Philippines',          flag: '🇵🇭', region: 'asia',     networks: 'Globe',                          image: _UNSPLASH('photo-1518509562904-e7ef99cdcc86') },
  { code: 'ES', name: 'Spain',                flag: '🇪🇸', region: 'europe',   networks: 'Movistar, Orange, Vodafone',     popular: true, image: _UNSPLASH('photo-1583422409516-2895a77efded') },
  { code: 'GB', name: 'United Kingdom',       flag: '🇬🇧', region: 'europe',   networks: 'Vodafone, EE, O2',               popular: true, image: _UNSPLASH('photo-1486299267070-83823f5448dd') },
  { code: 'CH', name: 'Switzerland',          flag: '🇨🇭', region: 'europe',   networks: 'Sunrise, Salt',                  image: _UNSPLASH('photo-1507039915464-9d829b6d2d78') },
  { code: 'SE', name: 'Sweden',               flag: '🇸🇪', region: 'europe',   networks: 'Tele2, Telenor, Telia',          image: _UNSPLASH('photo-1484037832928-afe345637f55') },
  { code: 'PT', name: 'Portugal',             flag: '🇵🇹', region: 'europe',   networks: 'MEO, Vodafone, NOS',             image: _UNSPLASH('photo-1536663815808-535e2280d2c2') },
  { code: 'NO', name: 'Norway',               flag: '🇳🇴', region: 'europe',   networks: 'Ice, Telenor, Telia',            image: _UNSPLASH('photo-1663428520845-056989f8a664') },
  { code: 'NL', name: 'Netherlands',          flag: '🇳🇱', region: 'europe',   networks: 'KPN, Vodafone, Odido',           image: _UNSPLASH('photo-1459679749680-18eb1eb37418') },
  { code: 'IE', name: 'Ireland',              flag: '🇮🇪', region: 'europe',   networks: 'eir, Vodafone',                  image: _UNSPLASH('photo-1557843961-b589ce207737') },
  { code: 'IS', name: 'Iceland',              flag: '🇮🇸', region: 'europe',   networks: 'Vodafone',                       image: _UNSPLASH('photo-1488415032361-b7e238421f1b') },
  { code: 'HU', name: 'Hungary',              flag: '🇭🇺', region: 'europe',   networks: 'One HU, Yettel',                 image: _UNSPLASH('photo-1507622560124-621e26755fb8') },
  { code: 'IL', name: 'Israel',               flag: '🇮🇱', region: 'asia',     networks: 'Pelephone, HOT Mobile',          image: _UNSPLASH('photo-1500990702037-7620ccb6a60a') },
  { code: 'DE', name: 'Germany',              flag: '🇩🇪', region: 'europe',   networks: 'O2-de, Vodafone, Telekom',       image: _UNSPLASH('photo-1552553302-9211bf7f7053') },
  { code: 'FI', name: 'Finland',              flag: '🇫🇮', region: 'europe',   networks: 'Elisa, Telia, DNA',              image: _UNSPLASH('photo-1566111947159-0fcbf19648bc') },
  { code: 'DK', name: 'Denmark',              flag: '🇩🇰', region: 'europe',   networks: 'TDC, Telenor, Telia',            image: _UNSPLASH('photo-1521240104483-b3e91583366c') },
  { code: 'HR', name: 'Croatia',              flag: '🇭🇷', region: 'europe',   networks: 'Telemach, A1, HT HR',            image: _UNSPLASH('photo-1626699748984-47d6942751ca') },
  { code: 'BG', name: 'Bulgaria',             flag: '🇧🇬', region: 'europe',   networks: 'A1, Vivacom, Yettel',            image: _UNSPLASH('photo-1621682372775-533449e550ed') },
  { code: 'AT', name: 'Austria',              flag: '🇦🇹', region: 'europe',   networks: 'A1, Magenta',                    image: _UNSPLASH('photo-1519923041107-e4dc8d9193da') },
  { code: 'AU', name: 'Australia',            flag: '🇦🇺', region: 'oceania',  networks: 'Optus',                          image: _UNSPLASH('photo-1528072164453-f4e8ef0d475a') },
]

export const REGIONAL_PLANS_META: RegionalMeta[] = [
  {
    code: 'CHM',
    name: 'China + HK + Macau',
    flag: '🇨🇳',
    region: 'china-region',
    networks: 'China Unicom, CSL, CTM',
    scope: '3 regions',
    coverageUnit: 'regions',
    icon: '🇨🇳',
    desc: 'Seamless coverage across mainland China, Hong Kong, and Macau — no VPN required for common apps.',
    image: _UNSPLASH('photo-1741135742394-97c2a5b61080'),
  },
  {
    code: 'EU',
    name: 'Europe',
    flag: '🌍',
    region: 'europe',
    networks: 'Orange, Vodafone, T-Mobile',
    scope: '38 countries',
    icon: '🌍',
    desc: 'One eSIM across Europe — online the moment you land.',
    featured: true,
    // Commissioned flat illustration — a composite European skyline rather
    // than one city's landmarks, which suits a 38-country bundle better than
    // any single photo could. Self-contained artwork: see RegionalMeta.artwork.
    // (Was Earth-at-night from space, before that a vintage map / Eiffel Tower.)
    image: '/img/eu-card.png',
    artwork: true,
  },
  {
    code: 'AP',
    name: 'Asia-Pacific',
    flag: '🌏',
    region: 'asia-pacific',
    networks: 'Docomo, SK Telecom, Telstra',
    scope: '15 destinations',
    icon: '🐉',
    desc: 'From Tokyo to Bali on one plan — great for multi-stop itineraries.',
    image: _UNSPLASH('photo-1741097574041-d70d3fe6a3ab'),
  },
  {
    code: 'ANZ',
    name: 'Australia + New Zealand',
    flag: '🇦🇺',
    region: 'oceania',
    networks: 'Telstra, Optus, Spark',
    scope: '2 countries',
    icon: '🇦🇺',
    desc: 'One eSIM for both Australia and New Zealand — pick up the same plan whether you start in Sydney or Auckland.',
    image: _UNSPLASH('photo-1610056352054-a68fe4f998e1'),
  },
  {
    code: 'NAM',
    name: 'USA + Canada + Mexico',
    flag: '🇺🇸',
    region: 'americas',
    networks: 'AT&T, Rogers, Telcel',
    scope: '3 countries',
    icon: '🛣️',
    desc: 'North America on a single plan — ideal for road trips that cross the border.',
    image: _UNSPLASH('photo-1560996043-f896c5844d32'),
  },
  {
    code: 'SEA',
    name: 'Thailand + Singapore + Malaysia + Indonesia',
    flag: '🌏',
    region: 'asia',
    networks: 'AIS, Simba, Digi, Telkomsel',
    scope: '4 countries',
    icon: '🛕',
    desc: 'Thailand, Singapore, Malaysia, and Indonesia on one eSIM — a cleaner four-country bundle than the broader Asia-Pacific plan.',
    image: _UNSPLASH('photo-1508964942454-1a56651d54ac'),
  },
  {
    code: 'SPG',
    name: 'Saipan + Guam',
    flag: '🇬🇺',
    region: 'oceania',
    networks: 'Docomo Pacific',
    scope: '2 territories',
    icon: '🌴',
    desc: 'Pacific-island coverage for Saipan and Guam — useful for diving trips and US-territory travel.',
    image: _UNSPLASH('photo-1603477849227-705c424d1d80'),
  },
  {
    code: 'SAM',
    name: 'South America',
    flag: '🌎',
    region: 'americas',
    networks: 'movistar, TIM, Vivo',
    scope: '8 destinations',
    icon: '🦙',
    desc: 'Brazil, Colombia, Argentina, Chile, Peru, Ecuador, Uruguay, and French Guiana on one plan — perfect for the Andes-to-Atlantic loop.',
    image: _UNSPLASH('photo-1587595431973-160d0d94add1'),
  },
  {
    code: 'AFR',
    name: 'Africa',
    flag: '🌍',
    region: 'africa',
    networks: 'Orange, Ooredoo, Telkom',
    scope: '6 destinations',
    icon: '🦁',
    desc: 'Algeria, Tunisia, Egypt, South Africa, Ghana, and Réunion on one eSIM.',
    image: _UNSPLASH('photo-1577971132997-c10be9372519'),
  },
]

// ---- Regional coverage (countries + networks per bundle) ----------------
// Exact roaming footprint for a regional eSIM, shown in the Coverage modal
// on the plan page. `nets` is the carrier list (slash-separated) sourced
// from the JoyTel pack sheet — these are the partners the eSIM roams onto,
// which can differ slightly from the standalone-country carrier lists.
export interface CoverageEntry {
  name: string
  flag: string
  nets: string
}

export const REGION_COVERAGE: Record<string, CoverageEntry[]> = {
  // China + HK + Macau — JoyTel "中港澳" pack. The three legs are NOT uniform:
  // Hong Kong is 4G-only, so it carries the "(4G)" marker that softens the
  // modal's speed line to "most networks". Note the mainland leg is China
  // Unicom here — the CN country plans run on China Mobile instead.
  CHM: [
    { name: 'China (Mainland)', flag: '🇨🇳', nets: 'China Unicom' },
    { name: 'Hong Kong', flag: '🇭🇰', nets: 'CSL (4G)' },
    { name: 'Macau', flag: '🇲🇴', nets: 'CTM' },
  ],
  // Europe — JoyTel "欧洲C" pack (capped + 10MAX; switched from 多地区F on
  // 2026-08-26, which had silently roamed the US and started customers'
  // clocks at home — see EUROPE PACK SPLIT in backend seed_catalog.py).
  // 欧洲C is Europe-only, so for the first time this list IS the pack's
  // full coverage: all 39 destinations, nothing hidden, nothing extra.
  // Carrier strings verbatim from the 欧洲C sheet (2026-08-25 screenshots).
  // Sorted alphabetically by English name at render time.
  EU: [
    { name: 'Albania', flag: '🇦🇱', nets: 'One.al / Vodafone' },
    { name: 'Austria', flag: '🇦🇹', nets: 'A1 / Magenta' },
    { name: 'Belgium', flag: '🇧🇪', nets: 'Orange / Proximus / Telenet' },
    { name: 'Bulgaria', flag: '🇧🇬', nets: 'A1 / Vivacom / Yettel' },
    { name: 'Croatia', flag: '🇭🇷', nets: 'Telemach / A1 / HT HR' },
    { name: 'Cyprus', flag: '🇨🇾', nets: 'epic' },
    { name: 'Czech Republic', flag: '🇨🇿', nets: 'O2-CZ / Vodafone' },
    { name: 'Denmark', flag: '🇩🇰', nets: 'TDC / Telenor / Telia' },
    { name: 'Estonia', flag: '🇪🇪', nets: 'Elisa / Telia / Tele2' },
    { name: 'Finland', flag: '🇫🇮', nets: 'Elisa / Telia / DNA' },
    { name: 'France', flag: '🇫🇷', nets: 'Orange / Bouygues / Free' },
    { name: 'Germany', flag: '🇩🇪', nets: 'O2-de / Vodafone / Telekom' },
    { name: 'Greece', flag: '🇬🇷', nets: 'Vodafone GR / Nova / Cosmote' },
    { name: 'Hungary', flag: '🇭🇺', nets: 'One HU / Yettel' },
    { name: 'Iceland', flag: '🇮🇸', nets: 'Vodafone' },
    { name: 'Ireland', flag: '🇮🇪', nets: 'eir / Vodafone' },
    { name: 'Italy', flag: '🇮🇹', nets: 'Vodafone IT / iliad' },
    { name: 'Latvia', flag: '🇱🇻', nets: 'Tele2 / LMT / Bite' },
    { name: 'Liechtenstein', flag: '🇱🇮', nets: 'FL1' },
    { name: 'Lithuania', flag: '🇱🇹', nets: 'Tele2 / Telia / Bite' },
    { name: 'Luxembourg', flag: '🇱🇺', nets: 'Tango / Orange' },
    { name: 'Malta', flag: '🇲🇹', nets: 'Melita / GO / Vodafone' },
    { name: 'Moldova', flag: '🇲🇩', nets: 'Orange / Moldcell / Moldtelecom' },
    { name: 'Montenegro', flag: '🇲🇪', nets: 'MTEL' },
    { name: 'Netherlands', flag: '🇳🇱', nets: 'KPN / Vodafone / Odido' },
    { name: 'Norway', flag: '🇳🇴', nets: 'ice / Telenor / Telia' },
    { name: 'Poland', flag: '🇵🇱', nets: 'Plus' },
    { name: 'Portugal', flag: '🇵🇹', nets: 'Vodafone P / NOS / MEO' },
    { name: 'Romania', flag: '🇷🇴', nets: 'DIGI.ro / Vodafone / Orange' },
    { name: 'Serbia', flag: '🇷🇸', nets: 'Yettel (4G)' },
    { name: 'Slovakia', flag: '🇸🇰', nets: 'O2-SK / Orange' },
    { name: 'Slovenia', flag: '🇸🇮', nets: 'A1 / Telemach' },
    { name: 'Spain', flag: '🇪🇸', nets: 'movistar / Orange / Vodafone' },
    { name: 'Sweden', flag: '🇸🇪', nets: 'Tele2 / Telenor / Telia' },
    { name: 'Switzerland', flag: '🇨🇭', nets: 'Salt / Sunrise' },
    { name: 'Turkey', flag: '🇹🇷', nets: 'Turk Telekom / Vodafone' },
    { name: 'Ukraine', flag: '🇺🇦', nets: 'Vodafone UA / Kyivstar' },
    { name: 'Vatican City', flag: '🇻🇦', nets: 'Vodafone IT / iliad' },
    { name: 'United Kingdom', flag: '🇬🇧', nets: 'Vodafone UK / EE / O2-UK' },
  ],
  // Thailand + Singapore + Malaysia + Indonesia — carriers from the JoyTel
  // 新马印泰 (XMTY) pack sheet, 2026-07-03.
  SEA: [
    { name: 'Thailand', flag: '🇹🇭', nets: 'AIS / True Move' },
    { name: 'Singapore', flag: '🇸🇬', nets: 'Simba / StarHub' },
    { name: 'Malaysia', flag: '🇲🇾', nets: 'Digi / Celcom / U Mobile' },
    { name: 'Indonesia', flag: '🇮🇩', nets: 'Telkomsel / XL / Indosat' },
  ],
  // Asia-Pacific — JoyTel "亚太多地区A" (JOY Asia-Pacific Multi-region A)
  // pack sheet, 2026-07-04. Most destinations are 5G/4G; a "(4G)" marker
  // on a carrier means that network tops out below 5G on this pack
  // (Vietnam and Cambodia are 4G-only). Markers stay slash-free because
  // the Coverage modal splits `nets` on "/" into per-carrier chips.
  AP: [
    { name: 'Japan', flag: '🇯🇵', nets: 'Rakuten / SoftBank / Docomo / KDDI' },
    { name: 'South Korea', flag: '🇰🇷', nets: 'SK Telecom / LG' },
    { name: 'Singapore', flag: '🇸🇬', nets: 'Simba / StarHub' },
    { name: 'Malaysia', flag: '🇲🇾', nets: 'Digi / Celcom / U Mobile' },
    { name: 'Thailand', flag: '🇹🇭', nets: 'AIS / True Move' },
    { name: 'Indonesia', flag: '🇮🇩', nets: 'Telkomsel / XL (4G) / Indosat' },
    { name: 'Philippines', flag: '🇵🇭', nets: 'Globe / Smart' },
    { name: 'Vietnam', flag: '🇻🇳', nets: 'Vinaphone (4G)' },
    { name: 'Cambodia', flag: '🇰🇭', nets: 'Metfone (4G) / Smart (4G)' },
    { name: 'Australia', flag: '🇦🇺', nets: 'Telstra' },
    { name: 'New Zealand', flag: '🇳🇿', nets: 'Spark' },
    { name: 'Mainland China', flag: '🇨🇳', nets: 'China Unicom / China Telecom (4G)' },
    { name: 'Hong Kong', flag: '🇭🇰', nets: '3 (Three)' },
    { name: 'Macau', flag: '🇲🇴', nets: '3 (Three)' },
    { name: 'Taiwan', flag: '🇹🇼', nets: 'Chunghwa' },
  ],
  // Saipan + Guam — JoyTel "塞班、关岛" pack sheet, 2026-07-04. One
  // carrier (Docomo Pacific) covers both territories at 5G/4G.
  SPG: [
    { name: 'Saipan', flag: '🇲🇵', nets: 'Docomo Pacific' },
    { name: 'Guam', flag: '🇬🇺', nets: 'Docomo Pacific' },
  ],
  // South America — JoyTel "南美A" (JOY South America A) pack sheet,
  // 2026-07-04. movistar is the backbone in 6 of 8 destinations;
  // Argentina, Ecuador, Peru, and French Guiana are 4G-only (marked).
  SAM: [
    { name: 'Brazil', flag: '🇧🇷', nets: 'TIM / Vivo' },
    { name: 'Argentina', flag: '🇦🇷', nets: 'movistar (4G)' },
    { name: 'Chile', flag: '🇨🇱', nets: 'WOM / movistar' },
    { name: 'Ecuador', flag: '🇪🇨', nets: 'movistar (4G)' },
    { name: 'Peru', flag: '🇵🇪', nets: 'movistar (4G)' },
    { name: 'Uruguay', flag: '🇺🇾', nets: 'movistar' },
    { name: 'Colombia', flag: '🇨🇴', nets: 'movistar' },
    { name: 'French Guiana', flag: '🇬🇫', nets: 'Digicel (4G) / Bouygues (4G)' },
  ],
  // Africa — JoyTel "非洲A" (JOY Africa A) pack sheet, 2026-07-04. Algeria,
  // South Africa, and Ghana are 4G-only on this pack (marked); the rest
  // are 5G/4G. Same pack as the Algeria country listings.
  AFR: [
    { name: 'Algeria', flag: '🇩🇿', nets: 'Djezzy (4G) / Ooredoo (4G)' },
    { name: 'Tunisia', flag: '🇹🇳', nets: 'Ooredoo / Tunisie Telecom' },
    { name: 'Egypt', flag: '🇪🇬', nets: 'Orange / etisalat' },
    { name: 'South Africa', flag: '🇿🇦', nets: 'Telkom (4G)' },
    { name: 'Ghana', flag: '🇬🇭', nets: 'AT — Airtel (4G)' },
    { name: 'Réunion', flag: '🇷🇪', nets: 'Orange / TELCO' },
  ],
}

// ---- Bundle search coverage ---------------------------------------------
// Extra country names each regional bundle covers, used by SEARCH matching
// only (modal-quality per-country carrier data lives in REGION_COVERAGE).
// Fills the gaps where the bundle's display name alone wouldn't match what
// a traveler types — e.g. "Brazil" → South America, "Hong Kong" → CHM.
// English names only. AP and AFR footprints aren't enumerated yet (pending
// their JoyTel pack sheets), so those bundles match by name only.
export const BUNDLE_SEARCH_COUNTRIES: Record<string, string[]> = {
  CHM: ['China', 'Hong Kong', 'Macau'],
  NAM: ['United States', 'USA', 'Canada', 'Mexico'],
  ANZ: ['Australia', 'New Zealand'],
  // Unaccented alias so typing "reunion" matches the Réunion coverage row.
  AFR: ['Reunion'],
}

// ---- Regions (for browse-page filtering) ----
export const REGIONS = [
  { id: 'all', label: 'All' },
  { id: 'asia', label: 'Asia' },
  { id: 'americas', label: 'Americas' },
  { id: 'europe', label: 'Europe' },
  { id: 'oceania', label: 'Oceania' },
  { id: 'africa', label: 'Africa' },
  { id: 'asia-pacific', label: 'Asia-Pacific' },
  { id: 'china-region', label: 'China + HK + Macau' },
] as const

// ---- Reviews (placeholder copy; replace with real testimonials later) ----
export const REVIEWS: Review[] = [
  {
    name: 'Danny Y',
    from: 'USA → Hong Kong',
    stars: 5,
    text: 'Landed at HKIA and was online before I cleared customs. Used it all week across Hong Kong without thinking about wifi once.',
  },
  {
    name: 'Alice W',
    from: 'USA → China',
    stars: 5,
    text: 'Worked everywhere I went in China — Beijing subway, the Great Wall, Shanghai. Way easier than buying a SIM card at the airport.',
  },
  {
    name: 'Felicity S',
    from: 'USA → Japan',
    stars: 5,
    text: 'Used it across Tokyo, Kyoto, and Osaka. Speeds held up even on the Shinkansen, and topping up an extra GB took thirty seconds.',
  },
]

// ---- Approximate city coordinates for the globe markers ----
export const CITY_COORDS: Record<string, { lng: number; lat: number }> = {
  US: { lng: -98.0, lat: 39.5 },
  JP: { lng: 139.69, lat: 35.68 },
  KR: { lng: 126.98, lat: 37.57 },
  CN: { lng: 116.4, lat: 39.9 },
  EU: { lng: 10.0, lat: 50.0 }, // Central Europe
  AP: { lng: 120.0, lat: 10.0 }, // SE Asia
}

// ---- Derived helpers ----

export function priceDollars(cents: number): string {
  return (cents / 100).toFixed(2)
}

export function formatData(gb: number): string {
  return gb >= 999 ? 'Unlimited' : `${gb} GB`
}

export function isUnlimited(p: Plan): boolean {
  return p.data_gb >= 999
}

export function plansForCountry(plans: Plan[], code: string): Plan[] {
  return plans
    .filter((p) => p.country === code)
    .sort((a, b) => a.data_gb - b.data_gb || a.validity_days - b.validity_days)
}

/** Cheapest price across a country's plans (in cents). */
export function fromPrice(plans: Plan[], code: string): number | null {
  const matches = plans.filter((p) => p.country === code)
  if (matches.length === 0) return null
  return Math.min(...matches.map((p) => p.price_cents))
}

/** Country codes that actually have at least one plan. */
export function availableCountryCodes(plans: Plan[]): string[] {
  return Array.from(new Set(plans.filter((p) => p.plan_type === 'country').map((p) => p.country)))
}
